import type { CSSProperties } from 'react';

/**
 * Sunstone-style "used / total (%)" cell with an inline progress bar. Reused
 * across host CPU/memory, datastore usage and network leases.
 * Bar color tracks utilization: success <70%, warning <90%, danger ≥90%.
 */
export interface UsageMeterProps {
  used: number;
  total: number;
  suffix?: string;
  /** Hide the textual "used / total" line and render just the bar. */
  barOnly?: boolean;
}

function barColor(percent: number): string {
  if (percent >= 90) return 'var(--vn-danger-solid)';
  if (percent >= 70) return 'var(--vn-warning-solid)';
  return 'var(--vn-success-solid)';
}

const track: CSSProperties = {
  height: 6,
  borderRadius: 'var(--vn-radius-full)',
  background: 'var(--vn-bg-muted, var(--vn-bg-subtle))',
  overflow: 'hidden',
};

export function UsageMeter({ used, total, suffix = '', barOnly = false }: UsageMeterProps) {
  const percent = total > 0 ? Math.round((used / total) * 100) : 0;
  return (
    <div style={{ width: '100%', minWidth: 140, display: 'grid', gap: 4 }}>
      {!barOnly && (
        <div
          style={{
            fontSize: 'var(--vn-font-size-sm)',
            color: 'var(--vn-fg-muted)',
            textAlign: 'right',
            lineHeight: 1.2,
          }}
        >
          {used.toLocaleString()}
          {suffix} / {total.toLocaleString()}
          {suffix} ({percent}%)
        </div>
      )}
      <div style={track}>
        <div
          style={{
            width: `${Math.min(percent, 100)}%`,
            height: '100%',
            background: barColor(percent),
            borderRadius: 'var(--vn-radius-full)',
          }}
        />
      </div>
    </div>
  );
}
